import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import AppLoading from '../components/AppLoading';
import { isAdminDashboard } from './DashboardLayoutPage';
import { useDashboardViews, DashboardView } from '../hooks/useDashboardViews';
import { graphVersionsAPIsService, GraphVersion, GraphWithVersions } from '../hooks/graphVersionsAPIs';

const GraphVersionManagementPage: React.FC = () => {
  const { dashboardViews, loading: dashboardsLoading, error: dashboardsError, refetch: refetchDashboards } = useDashboardViews(true, true);
  const [selectedDashboardId, setSelectedDashboardId] = useState<number | null>(null);
  const [graphs, setGraphs] = useState<GraphWithVersions[]>([]);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showVersionDropdown, setShowVersionDropdown] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<{ graphId: number; version: GraphVersion } | null>(null);
  const [showAddVersionModal, setShowAddVersionModal] = useState<number | null>(null);
  const [showEditVersionModal, setShowEditVersionModal] = useState<{ graphId: number; version: GraphVersion } | null>();
  const [newVersionName, setNewVersionName] = useState('');
  const [newVersionDescription, setNewVersionDescription] = useState('');
  const [editVersionName, setEditVersionName] = useState('');
  const [editVersionDescription, setEditVersionDescription] = useState('');

  const orderedDashboardViews = [
    ...dashboardViews.filter((dashboard) => !isAdminDashboard(dashboard)),
    ...dashboardViews.filter((dashboard) => isAdminDashboard(dashboard)),
  ];

  // Update graphs when dashboard is selected from existing dashboardViews data
  useEffect(() => {
    if (selectedDashboardId && dashboardViews.length > 0) {
      const selectedDashboard = dashboardViews.find((d: DashboardView) => d.id === selectedDashboardId);
      
      if (selectedDashboard && selectedDashboard.graphs) {
        // Graphs already have versions from the API response
        setGraphs(selectedDashboard.graphs.map((graph: any) => ({
          ...graph,
          versions: graph.versions || []
        })));
      } else {
        setGraphs([]);
      }
    } else {
      setGraphs([]);
    }
  }, [selectedDashboardId, dashboardViews]);

  const handleSelectVersion = (graphId: number, version: GraphVersion) => {
    setShowVersionDropdown(null);
    
    // Find the graph to check current selected version
    const graph = graphs.find(g => g.id === graphId);
    const currentSelectedVersion = graph?.selectedVersion || 1;
    
    // If user selects the current version, do nothing
    if (version.version === currentSelectedVersion) {
      return;
    }
    
    setShowConfirmModal({ graphId, version });
  };

  const handleConfirmVersionSwitch = async () => {
    if (!showConfirmModal) return;

    try {
      setIsUpdating(true);
      await graphVersionsAPIsService.selectGraphVersion(showConfirmModal.graphId, showConfirmModal.version.version);
      toast.success(`Graph version switched to ${showConfirmModal.version.name} (v${showConfirmModal.version.version})`);
      setShowConfirmModal(null);
      // Refetch dashboard views to get updated selectedVersion
      await refetchDashboards();
    } catch (error) {
      console.error('Error switching version:', error);
      toast.error('Failed to switch graph version');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddVersion = async () => {
    if (!showAddVersionModal || !newVersionName.trim()) {
      toast.error('Please provide a version name');
      return;
    }

    try {
      setIsUpdating(true);
      
      // Get current versions to determine next version number
      const currentVersions = await graphVersionsAPIsService.getGraphVersions(showAddVersionModal);
      const nextVersion = currentVersions.length > 0 
        ? Math.max(...currentVersions.map(v => v.version)) + 1 
        : 2; // Version 1 is default, so new versions start from 2

      await graphVersionsAPIsService.createGraphVersion({
        dashboardGraphId: showAddVersionModal,
        version: nextVersion,
        name: newVersionName.trim(),
        description: newVersionDescription.trim() || undefined
      });

      toast.success(`Version ${nextVersion} created successfully`);
      setShowAddVersionModal(null);
      setNewVersionName('');
      setNewVersionDescription('');
      // Refetch dashboard views to get the new version
      await refetchDashboards();
    } catch (error: any) {
      console.error('Error adding version:', error);
      toast.error(error?.message || 'Failed to add version');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditVersion = (graphId: number, version: GraphVersion, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowVersionDropdown(null);
    setEditVersionName(version.name);
    setEditVersionDescription(version.description || '');
    setShowEditVersionModal({ graphId, version });
  };

  const handleUpdateVersion = async () => {
    if (!showEditVersionModal || !editVersionName.trim()) {
      toast.error('Please provide a version name');
      return;
    }

    try {
      setIsUpdating(true);
      
      if (!showEditVersionModal.version.id) {
        toast.error('Cannot update default version 1');
        return;
      }

      await graphVersionsAPIsService.updateGraphVersion(showEditVersionModal.version.id, {
        name: editVersionName.trim(),
        description: editVersionDescription.trim() || undefined
      });

      toast.success(`Version ${showEditVersionModal.version.version} updated successfully`);
      setShowEditVersionModal(null);
      setEditVersionName('');
      setEditVersionDescription('');
      // Refetch dashboard views to get the updated version
      await refetchDashboards();
    } catch (error: any) {
      console.error('Error updating version:', error);
      toast.error(error?.message || 'Failed to update version');
    } finally {
      setIsUpdating(false);
    }
  };


  const getCurrentVersionInfo = (graph: GraphWithVersions) => {
    const selectedVersion = graph.selectedVersion || 1;
    const version = graph.versions?.find(v => v.version === selectedVersion);
    return version || { version: selectedVersion, name: 'v1', description: 'Default version' };
  };

  const getAvailableVersions = (graph: GraphWithVersions): GraphVersion[] => {
    const versions = graph.versions || [];
    const hasVersion1 = versions.some(v => v.version === 1);
    
    // Always include version 1 as default (even if it exists in DB, show it first)
    const defaultV1: GraphVersion = hasVersion1 
      ? versions.find(v => v.version === 1)!
      : { id: 0, dashboardGraphId: graph.id, version: 1, name: 'v1', description: 'Default version' } as GraphVersion;
    
    // Get all other versions (excluding v1 if it exists in DB)
    const otherVersions = versions.filter(v => v.version !== 1);
    
    // Return v1 first, then all other versions sorted by version number
    return [
      defaultV1,
      ...otherVersions.sort((a, b) => a.version - b.version)
    ];
  };

  if (dashboardsLoading && dashboardViews.length === 0) {
    return (
      <div className="graph-version-management-page">
        <div className="loading-container">
          <AppLoading />
          <p>Loading dashboards...</p>
        </div>
      </div>
    );
  }

  if (dashboardsError) {
    return (
      <div className="graph-version-management-page">
        <div className="error-container">
          <h3>Error loading dashboards</h3>
          <p>{dashboardsError}</p>
          <button onClick={refetchDashboards} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="graph-version-management-page">
      <div className="layout-header">
        <div className="header-content">
          <h2>Graph Version Management</h2>
          <p>Select a dashboard to manage graph API versions</p>
        </div>
      </div>

      <div className="layout-content">
        {/* Dashboard Selection */}
        <div className="dashboard-selector">
          <label htmlFor="dashboard-select">Select Dashboard:</label>
          <select
            id="dashboard-select"
            value={selectedDashboardId || ''}
            onChange={(e) => setSelectedDashboardId(e.target.value ? parseInt(e.target.value) : null)}
            className="dashboard-select"
          >
            <option value="">-- Select a dashboard --</option>
            {orderedDashboardViews.map((dashboard) => (
              <option
                key={dashboard.id}
                value={dashboard.id}
                className={isAdminDashboard(dashboard) ? 'admin-dashboard-option' : undefined}
              >
                {dashboard.name}
              </option>
            ))}
          </select>
        </div>

        {/* Graphs Grid */}
        {selectedDashboardId && (
          <>
            {loading ? (
              <div className="loading-container">
                <AppLoading />
                <p>Loading graphs...</p>
              </div>
            ) : graphs.length === 0 ? (
              <div className="empty-state">
                <p>No graphs found for this dashboard</p>
              </div>
            ) : (
              <div className="graphs-container">
                {graphs.map((graph) => {
                  const currentVersion = getCurrentVersionInfo(graph);
                  const availableVersions = getAvailableVersions(graph);

                  return (
                    <div 
                      key={graph.id} 
                      className={`graph-card ${showVersionDropdown === graph.id ? 'dropdown-open' : ''}`}
                      onMouseLeave={() => setShowVersionDropdown(null)}
                    >
                      <div className="graph-header">
                        <h3 className="graph-title">{graph.title}</h3>
                        <span className="version-tag">
                          {`v${currentVersion.version}`}
                        </span>
                      </div>
                      
                      <div className="graph-info">
                        <div className="version-info">
                          <div className="version-name">
                            <strong>Name:</strong> {currentVersion.name}
                          </div>
                          {currentVersion.description && (
                            <div className="version-description">
                              <strong>Description:</strong> {currentVersion.description}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="graph-actions">
                        <div className="version-selector-wrapper">
                          <button
                            className="btn btn-primary switch-version-btn"
                            onClick={() => setShowVersionDropdown(
                              showVersionDropdown === graph.id ? null : graph.id
                            )}
                            disabled={isUpdating}
                          >
                            Switch Version
                          </button>
                          
                          {showVersionDropdown === graph.id && (
                            <div className="version-dropdown">
                              {availableVersions.map((version) => {
                                const isDefaultVersion = version.version === 1;
                                const isCurrentlySelected = version.version === (graph.selectedVersion || 1);
                                const canEdit = !isDefaultVersion || version.id !== undefined;

                                return (
                                  <div
                                    key={version.id || `v${version.version}`}
                                    className={`version-option ${isCurrentlySelected ? 'active' : ''}`}
                                  >
                                    <div 
                                      className="version-option-content"
                                      onClick={() => handleSelectVersion(graph.id, version)}
                                    >
                                      <div className="version-option-header">
                                        <span className="version-number">v{version.version}</span>
                                        <span className="version-name-text">
                                          {version.name}
                                          <br />
                                          {version.description && (
                                            <span className="version-description-inline">{version.description}</span>
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                    {canEdit && (
                                      <div className="version-option-actions" onClick={(e) => e.stopPropagation()}>
                                        <button
                                          className="version-action-btn edit-btn"
                                          onClick={(e) => handleEditVersion(graph.id, version, e)}
                                          title="Edit version"
                                          disabled={isUpdating}
                                        >
                                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11.333 2.00001C11.5084 1.82449 11.7163 1.68508 11.9448 1.58927C12.1733 1.49346 12.418 1.44312 12.6663 1.44312C12.9146 1.44312 13.1593 1.49346 13.3878 1.58927C13.6163 1.68508 13.8242 1.82449 13.9997 2.00001C14.1752 2.17553 14.3146 2.38345 14.4104 2.61194C14.5062 2.84043 14.5566 3.08509 14.5566 3.33334C14.5566 3.58159 14.5062 3.82625 14.4104 4.05474C14.3146 4.28323 14.1752 4.49115 13.9997 4.66668L5.33301 13.3333L1.33301 14.6667L2.66634 10.6667L11.333 2.00001Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <button
                          className="btn btn-secondary add-version-btn"
                          onClick={() => setShowAddVersionModal(graph.id)}
                          disabled={isUpdating}
                        >
                          Add New Version
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {!selectedDashboardId && (
          <div className="empty-state">
            <p>Please select a dashboard to view and manage graph versions</p>
          </div>
        )}
      </div>

      {/* Confirm Version Switch Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Version Switch</h3>
            <div className="confirm-details">
              <p><strong>Version Number:</strong> {showConfirmModal.version.version}</p>
              <p><strong>Name:</strong> {showConfirmModal.version.name}</p>
              {showConfirmModal.version.description && (
                <p><strong>Description:</strong> {showConfirmModal.version.description}</p>
              )}
            </div>
            <p className="confirm-message">Are you sure you want to switch to this version?</p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(null)}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmVersionSwitch}
                disabled={isUpdating}
              >
                {isUpdating ? 'Switching...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Version Modal */}
      {showAddVersionModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddVersionModal(null);
          setNewVersionName('');
          setNewVersionDescription('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Version</h3>
            <div className="form-group">
              <label htmlFor="version-name">Version Name *</label>
              <input
                id="version-name"
                type="text"
                value={newVersionName}
                onChange={(e) => setNewVersionName(e.target.value)}
                placeholder="Enter version name"
                className="form-input"
                disabled={isUpdating}
              />
            </div>
            <div className="form-group">
              <label htmlFor="version-description">Description (Optional)</label>
              <textarea
                id="version-description"
                value={newVersionDescription}
                onChange={(e) => setNewVersionDescription(e.target.value)}
                placeholder="Enter version description"
                className="form-textarea"
                rows={3}
                disabled={isUpdating}
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddVersionModal(null);
                  setNewVersionName('');
                  setNewVersionDescription('');
                }}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddVersion}
                disabled={isUpdating || !newVersionName.trim()}
              >
                {isUpdating ? 'Adding...' : 'Add Version'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Version Modal */}
      {showEditVersionModal && (
        <div className="modal-overlay" onClick={() => {
          setShowEditVersionModal(null);
          setEditVersionName('');
          setEditVersionDescription('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Version</h3>
            <div className="confirm-details">
              <p><b>Version Number:</b> {showEditVersionModal.version.version}</p>
            </div>
            <div className="form-group">
              <label htmlFor="edit-version-name">Version Name *</label>
              <input
                id="edit-version-name"
                type="text"
                value={editVersionName}
                onChange={(e) => setEditVersionName(e.target.value)}
                placeholder="Enter version name"
                className="form-input"
                disabled={isUpdating}
              />
            </div>
            <div className="form-group">
              <label htmlFor="edit-version-description">Description (Optional)</label>
              <textarea
                id="edit-version-description"
                value={editVersionDescription}
                onChange={(e) => setEditVersionDescription(e.target.value)}
                placeholder="Enter version description"
                className="form-textarea"
                rows={3}
                disabled={isUpdating}
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowEditVersionModal(null);
                  setEditVersionName('');
                  setEditVersionDescription('');
                }}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateVersion}
                disabled={isUpdating || !editVersionName.trim()}
              >
                {isUpdating ? 'Updating...' : 'Update Version'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default GraphVersionManagementPage;

